import { join, relative, resolve } from 'node:path';

/** Join under a runtime root without Turbopack NFT tracing the monorepo tree. */
function joinUnderRuntimeRoot(root: string, ...segments: string[]): string {
  return join(/* turbopackIgnore: true */ root, ...segments);
}

/** Resolve under a runtime root without Turbopack NFT tracing the monorepo tree. */
function resolveUnderRuntimeRoot(root: string, ...segments: string[]): string {
  return resolve(/* turbopackIgnore: true */ root, ...segments);
}

/** Railway / chat canonical workspace prefix (not necessarily the on-disk root locally). */
export const CANONICAL_WORKSPACE_PREFIX = '/workspace';

/** Env var for the org repo folder name under each user workspace (e.g. `uvilo-os`). */
export const ORG_REPO_ENV_KEY = 'NEXT_PUBLIC_ORG_REPO';

/** Env var for the prod repo folder name under each user workspace (e.g. `uvilo-mono`). */
export const PROD_REPO_ENV_KEY = 'NEXT_PUBLIC_PROD_REPO';

export type ParsedPortableWorkspacePath = {
  readonly repoName: string;
  readonly relativePath: string;
};

/** Subset of process env used by workspace path helpers (all keys optional). */
export type WorkspacePathEnv = {
  readonly WORKSPACE_ROOT?: string | undefined;
  readonly NEXT_PUBLIC_ORG_REPO?: string | undefined;
  readonly NEXT_PUBLIC_PROD_REPO?: string | undefined;
};

/** Reads workspace path env vars from `process.env` without relying on global `ProcessEnv` augmentation. */
export function readWorkspacePathEnvFromProcess(): WorkspacePathEnv {
  return {
    WORKSPACE_ROOT: process.env.WORKSPACE_ROOT,
    NEXT_PUBLIC_ORG_REPO: process.env.NEXT_PUBLIC_ORG_REPO,
    NEXT_PUBLIC_PROD_REPO: process.env.NEXT_PUBLIC_PROD_REPO,
  };
}

/**
 * Resolves the on-disk workspace root from `WORKSPACE_ROOT` (default `/workspace` on Railway).
 */
export function resolveWorkspaceRootFromEnv(
  env: WorkspacePathEnv = readWorkspacePathEnvFromProcess(),
): string {
  const fromEnv = env.WORKSPACE_ROOT?.trim();
  return resolveUnderRuntimeRoot(fromEnv || CANONICAL_WORKSPACE_PREFIX);
}

/** Returns the configured org repo folder name, if any. */
export function getOrgRepoName(
  env: WorkspacePathEnv = readWorkspacePathEnvFromProcess(),
): string | undefined {
  return env.NEXT_PUBLIC_ORG_REPO;
}

/** Returns the configured prod repo folder name, if any. */
export function getProdRepoName(
  env: WorkspacePathEnv = readWorkspacePathEnvFromProcess(),
): string | undefined {
  return env.NEXT_PUBLIC_PROD_REPO;
}

/** Whether `repoName` matches a configured repo env var on this host. */
export function isConfiguredRepoName(
  repoName: string,
  env: WorkspacePathEnv = readWorkspacePathEnvFromProcess(),
): boolean {
  const orgRepo = getOrgRepoName(env);
  const prodRepo = getProdRepoName(env);
  return repoName === orgRepo || repoName === prodRepo;
}

/** Canonical per-user workspace path: `/workspace/{email}`. */
export function getUserWorkspaceCanonical(email: string): string {
  return `${CANONICAL_WORKSPACE_PREFIX}/${email}`;
}

/** Canonical repo root: `/workspace/{email}/{repoName}`. */
export function getRepoRootCanonical(email: string, repoName: string): string {
  return `${getUserWorkspaceCanonical(email)}/${repoName}`;
}

/**
 * Parses a portable path (`uvilo-os/Forge/foo.md`) into repo name and remainder.
 */
export function parsePortableWorkspacePath(path: string): ParsedPortableWorkspacePath | null {
  const trimmed = path.trim().replace(/^\/+/, '');
  if (!trimmed || trimmed.includes('@')) {
    return null;
  }
  if (trimmed === 'workspace' || trimmed.startsWith('workspace/')) {
    return null;
  }

  const slashIndex = trimmed.indexOf('/');
  if (slashIndex === -1) {
    return { repoName: trimmed, relativePath: '' };
  }

  const repoName = trimmed.slice(0, slashIndex);
  if (!repoName) {
    return null;
  }

  return {
    repoName,
    relativePath: trimmed.slice(slashIndex + 1),
  };
}

/**
 * Maps chat paths under `/workspace/...` to the configured on-disk `WORKSPACE_ROOT`.
 */
export function remapCanonicalWorkspacePath(
  path: string,
  workspaceRoot: string = resolveWorkspaceRootFromEnv(),
): string {
  if (path === CANONICAL_WORKSPACE_PREFIX) {
    return workspaceRoot;
  }
  if (path.startsWith(`${CANONICAL_WORKSPACE_PREFIX}/`)) {
    return joinUnderRuntimeRoot(workspaceRoot, path.slice(CANONICAL_WORKSPACE_PREFIX.length + 1));
  }
  return path;
}

/**
 * Replaces canonical `/workspace/` prefixes with the on-disk workspace root in free-form text
 * (e.g. shell commands that echo paths from chat context).
 */
export function remapCanonicalWorkspacePathsInText(
  text: string,
  workspaceRoot: string = resolveWorkspaceRootFromEnv(),
): string {
  if (
    resolveUnderRuntimeRoot(workspaceRoot) === resolveUnderRuntimeRoot(CANONICAL_WORKSPACE_PREFIX)
  ) {
    return text;
  }
  return text.replaceAll(`${CANONICAL_WORKSPACE_PREFIX}/`, `${workspaceRoot}/`);
}

/**
 * Converts a portable path to the canonical `/workspace/{email}/...` form.
 * @throws When the path is invalid or email is required but missing.
 */
export function portablePathToCanonical(portablePath: string, email: string): string {
  const trimmed = portablePath.trim();
  if (
    trimmed === CANONICAL_WORKSPACE_PREFIX ||
    trimmed.startsWith(`${CANONICAL_WORKSPACE_PREFIX}/`)
  ) {
    return trimmed.replace(/\/+$/, '') || CANONICAL_WORKSPACE_PREFIX;
  }
  if (trimmed.startsWith('/')) {
    throw new Error(`Invalid workspace path: ${portablePath}`);
  }

  const parsed = parsePortableWorkspacePath(trimmed);
  if (!parsed) {
    const exampleRepo = getOrgRepoName() ?? 'repo-name';
    throw new Error(`Portable path must start with {repoName}/, e.g. ${exampleRepo}/Forge/foo.md`);
  }

  if (!isConfiguredRepoName(parsed.repoName)) {
    const orgRepo = getOrgRepoName();
    if (orgRepo) {
      return `${getRepoRootCanonical(email, orgRepo)}/${trimmed}`;
    }
  }

  const base = getRepoRootCanonical(email, parsed.repoName);
  return parsed.relativePath ? `${base}/${parsed.relativePath}` : base;
}

/**
 * Strips `/workspace/{email}/` from a canonical path to a portable `{repoName}/...` path.
 */
export function canonicalPathToPortable(canonicalPath: string, email?: string): string {
  const normalized = canonicalPath.replace(/\/+$/, '');
  if (!normalized.startsWith(`${CANONICAL_WORKSPACE_PREFIX}/`)) {
    return canonicalPath;
  }

  const withoutPrefix = normalized.slice(CANONICAL_WORKSPACE_PREFIX.length + 1);
  if (email) {
    const userPrefix = `${email}/`;
    if (withoutPrefix.startsWith(userPrefix)) {
      return withoutPrefix.slice(userPrefix.length);
    }
  }

  const emailMatch = withoutPrefix.match(/^[^/]+@[^/]+\/(.+)$/);
  if (emailMatch?.[1]) {
    return emailMatch[1];
  }

  return withoutPrefix;
}

/**
 * Parses a resolved on-disk path under `diskWorkspaceRoot` into per-user workspace root and
 * path relative to that user directory (includes repo folder, e.g. `uvilo-os/Forge/foo.md`).
 * @throws When the path is not under `{diskWorkspaceRoot}/{email}/...`.
 */
export function splitUserWorkspacePath(
  absPath: string,
  diskWorkspaceRoot: string = resolveWorkspaceRootFromEnv(),
): { userWorkspaceRoot: string; relPath: string } {
  const relFromGlobal = relative(diskWorkspaceRoot, absPath);
  if (relFromGlobal.startsWith('..') || relFromGlobal === '..') {
    throw new Error('Path is not under workspace root');
  }
  const slashIndex = relFromGlobal.indexOf('/');
  const userSegment = slashIndex === -1 ? relFromGlobal : relFromGlobal.slice(0, slashIndex);
  if (!userSegment.includes('@')) {
    throw new Error(
      `Path must be under a user workspace (/workspace/{email}/...), got: ${relFromGlobal}`,
    );
  }
  const userWorkspaceRoot = joinUnderRuntimeRoot(diskWorkspaceRoot, userSegment);
  const relPath = slashIndex === -1 ? '' : relFromGlobal.slice(slashIndex + 1);
  if (!relPath) {
    throw new Error('Cannot operate on user workspace root');
  }
  return { userWorkspaceRoot, relPath };
}

/**
 * Normalizes a portable or canonical workspace file path for forge-filesystem.
 */
export function normalizeWorkspaceFilePath(rawPath: string, userEmail: string | undefined): string {
  const trimmed = rawPath.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (
    trimmed === CANONICAL_WORKSPACE_PREFIX ||
    trimmed.startsWith(`${CANONICAL_WORKSPACE_PREFIX}/`)
  ) {
    return trimmed;
  }
  if (!userEmail) {
    return trimmed;
  }
  return portablePathToCanonical(trimmed, userEmail);
}

/**
 * Resolves a workspace-relative, portable, or canonical path under `WORKSPACE_ROOT` on disk.
 * @throws When the resolved path escapes the workspace root.
 */
export function resolveWithinWorkspaceRoot(
  path: string,
  workspaceRoot: string = resolveWorkspaceRootFromEnv(),
): string {
  const remapped = remapCanonicalWorkspacePath(path, workspaceRoot);
  const absolute = resolveUnderRuntimeRoot(workspaceRoot, remapped);
  const relativePath = relative(workspaceRoot, absolute);
  if (relativePath.startsWith('..') || relativePath === '..') {
    throw new Error(`Path escapes workspace root: ${path}`);
  }
  return absolute;
}

/**
 * Resolves a configured repo name to its on-disk root for the given user.
 */
export function getWorkspaceRepoPhysicalPath(
  email: string,
  repoName: string,
  workspaceRoot: string = resolveWorkspaceRootFromEnv(),
): string {
  const canonical = getRepoRootCanonical(email, repoName);
  return resolveWithinWorkspaceRoot(canonical, workspaceRoot);
}
